import { z } from 'zod'
import { dataNominal, uuid, uuidDeRota } from './comum.js'
import { referenciaDeUsuario } from './usuarios.js'

export const prioridadeDaTarefa = z.enum(['baixa','média','alta'])
export type PrioridadeDaTarefa = z.infer<typeof prioridadeDaTarefa>

export const tipoDeVinculoDaTarefa = z.enum([
  'empresa','contato','negocio','pedido_venda','campanha','fornecedor','colaborador','orcamento_versao',
])
export type TipoDeVinculoDaTarefa = z.infer<typeof tipoDeVinculoDaTarefa>

export const estagioDaTarefa = z.object({
  id: uuid,
  nome: z.string(),
  ordem: z.number().int(),
  conclui: z.boolean(),
  ativo: z.boolean(),
})
export type EstagioDaTarefa = z.infer<typeof estagioDaTarefa>

/**
 * O rótulo do vínculo cujo registro foi excluído, ou que a pessoa não alcança.
 *
 * Mora no contrato porque DOIS lados leem o mesmo texto: a API o escreve no lugar do nome
 * (`tarefas.controller.ts`), e a tela o reconhece para não transformar em link um vínculo
 * que não abre nada (o nome vira texto, e não link morto: D-136, DS-533). Uma cópia do
 * texto na tela divergiria da API no dia em que alguém corrigisse uma vírgula.
 */
export const ROTULO_DE_VINCULO_INDISPONIVEL = 'Vínculo excluído ou indisponível'

export const vinculoDaTarefa = z.object({
  tipo: tipoDeVinculoDaTarefa,
  id: uuidDeRota,
  rotulo: z.string(),
})
export type VinculoDaTarefa = z.infer<typeof vinculoDaTarefa>

export const resumoDaTarefa = z.object({
  id: uuid,
  codigo: z.string().regex(/^TSK-\d{3,}$/),
  titulo: z.string(),
  prioridade: prioridadeDaTarefa,
  prazo: dataNominal.nullable(),
  duracaoEstimadaH: z.string().nullable(),
  concluidaEm: z.string().nullable(),
  /** Instante UTC; concluidaEm mantém a hora de parede legada de São Paulo. */
  concluidaEmInstante: z.iso.datetime().nullable().optional(),
  estagio: estagioDaTarefa,
  responsavel: referenciaDeUsuario.nullable(),
  criadaPorId: uuid.nullable(),
  podeEditar: z.boolean(),
  primeiroVinculo: vinculoDaTarefa.nullable(),
  subtarefas: z.object({ total: z.number().int().nonnegative(), concluidas: z.number().int().nonnegative() }),
})
export type ResumoDaTarefa = z.infer<typeof resumoDaTarefa>

export const tarefa = resumoDaTarefa.extend({
  descricao: z.string().nullable(),
  descricaoRica: z.string().nullable().optional(),
  paiId: uuid.nullable(),
  vinculos: z.array(vinculoDaTarefa),
  filhas: z.array(resumoDaTarefa),
  podeEditar: z.boolean(),
  podeExcluir: z.boolean(),
})
export type Tarefa = z.infer<typeof tarefa>

const camposDaTarefa = {
  titulo: z.string().trim().min(1),
  descricao: z.string().nullable().optional(),
  descricaoRica: z.string().nullable().optional(),
  responsavelId: uuidDeRota.nullable().optional(),
  estagioId: uuidDeRota,
  prioridade: prioridadeDaTarefa,
  prazo: dataNominal.nullable().optional(),
  duracaoEstimadaH: z.string().regex(/^\d+(?:\.\d{1,2})?$/).nullable().optional(),
  paiId: uuidDeRota.nullable().optional(),
}
export const entradaDeTarefa = z.object(camposDaTarefa).extend({ prioridade: prioridadeDaTarefa.default('média') })
export const entradaDeEdicaoDeTarefa = z.object(camposDaTarefa).partial()
export type EntradaDeTarefa = z.input<typeof entradaDeTarefa>
export type EntradaDeEdicaoDeTarefa = z.input<typeof entradaDeEdicaoDeTarefa>
export const entradaDeMovimentoDeTarefa = z.object({ estagioId: uuidDeRota })
export const entradaDeVinculosDaTarefa = z.object({
  vinculos: z.array(z.object({ tipo: tipoDeVinculoDaTarefa, id: uuidDeRota })),
})

export const alocacaoDaTarefa = z.object({
  id: uuid,
  tarefa: resumoDaTarefa,
  usuario: z.object({ id: uuid, nome: z.string() }),
  dia: dataNominal,
  horas: z.string(),
  podeEditar: z.boolean(),
})
export type AlocacaoDaTarefa = z.infer<typeof alocacaoDaTarefa>

export const entradaDeAlocacao = z.object({
  tarefaId: uuidDeRota,
  usuarioId: uuidDeRota,
  dia: dataNominal,
  horas: z.string().regex(/^(?:0\.5|[1-9]|1[0-2])(?:\.0|\.5)?$/),
})
export type EntradaDeAlocacao = z.input<typeof entradaDeAlocacao>
